import { stringToEnum } from '@snap-types/models/common';

export enum OriginalSource {
  DWH_STG_VIS = 'DWH_STG_VIS',
  DWH_STG_MWIFAS = 'DWH_STG_MWIFAS',
  SNAP = 'SNAP',
}

export const originalSourceSchema = stringToEnum(OriginalSource);

export enum SnapQuoteLineSource {
  PART_PICKER = 'PART_PICKER',
  IMPORT = 'IMPORT',
  CONFIGURATOR = 'CONFIGURATOR',
}

export const snapQuoteLineSourceSchema = stringToEnum(SnapQuoteLineSource);

export enum RunRateType {
  DAYS_PER_LOAD = 'DAYS_PER_LOAD',
  HRS_PER_PC = 'HRS_PER_PC',
  PCS_PER_MIN = 'PCS_PER_MIN',
  HRS_PER_LOAD = 'HRS_PER_LOAD',
  PCS_PER_HR = 'PCS_PER_HR',
  PCS_PER_DAY = 'PCS_PER_DAY',
  MIN_PER_PC = 'MIN_PER_PC',
  DAYS_PER_PC = 'DAYS_PER_PC',
  LOADS_PER_HR = 'LOADS_PER_HR',
  LOADS_PER_DAY = 'LOADS_PER_DAY',
}

export const runRateTypeSchema = stringToEnum(RunRateType);

export enum ConfigStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export const configStatusSchema = stringToEnum(ConfigStatus);

export enum ConfigEntities {
  SITE = 'SITE',
  USER = 'USER',
  CUSTOMER = 'CUSTOMER',
  PART = 'PART',
  QUOTE = 'QUOTE',
  QUOTE_LINE = 'QUOTE_LINE',
  QTY_BRK = 'QTY_BRK',
  REQ_PART = 'REQ_PART',
}

export const configEntitiesSchema = stringToEnum(ConfigEntities);

export enum ScrapYieldType {
  SCRAP = 'SCRAP',
  YIELD = 'YIELD',
}

export const scrapYieldTypeSchema = stringToEnum(ScrapYieldType);

export const scrapYieldTypeLabelMapping = {
  SCRAP: 'Scrap',
  YIELD: 'Yield',
} as const satisfies Record<ScrapYieldType, string>;

export enum QtyPerType {
  START = 'START',
  END = 'END',
}

export const qtyPerTypeSchema = stringToEnum(QtyPerType);

export enum QuoteBuilderVersion {
  V1 = 'V1',
  V2 = 'V2',
}

export const quoteBuilderVersionSchema = stringToEnum(QuoteBuilderVersion);

export enum PriceStrategy {
  CAPPriceSegmentation = 'CAPPriceSegmentation',
  FixedCustomer = 'FixedCustomer',
  FixedDefault = 'FixedDefault',
  FixedDiscount = 'FixedDiscount',
  FixedMarket = 'FixedMarket',
  NewPart = 'NewPart',
  NewPartUserCost = 'NewPartUserCost',
  NewPartUserPrice = 'NewPartUserPrice',
  PriceSegmentation = 'PriceSegmentation',
  RAFFixedMarket = 'RAFFixedMarket',
  RAFTargetGrossMargin = 'RAFTargetGrossMargin',
  TargetContributionMargin = 'TargetContributionMargin',
  TargetGrossMargin = 'TargetGrossMargin',
  UserImport = 'UserImport',
  UserPrice = 'UserPrice',
}

export const priceStrategySchema = stringToEnum(PriceStrategy);

export const PriceStrategiesThatRelyOnUnitCost = [
  PriceStrategy.CAPPriceSegmentation,
  PriceStrategy.PriceSegmentation,
  PriceStrategy.RAFTargetGrossMargin,
  PriceStrategy.TargetContributionMargin,
  PriceStrategy.TargetGrossMargin,
];

export enum LeadTimeStrategy {
  PLANNING_LEADTIME = 'PLANNING_LEADTIME',
  CONSTANT_VALUE = 'CONSTANT_VALUE',
  RAF_FIXED_MARKET = 'RAF_FIXED_MARKET',
}

export const leadTimeStrategySchema = stringToEnum(LeadTimeStrategy);

export enum UnitsPerPieceType {
  START = 'START',
  END = 'END',
}

export const unitsPerPieceTypeSchema = stringToEnum(UnitsPerPieceType);

export enum CustomerPotentialRanking {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
}

export const customerPotentialRankingSchema = stringToEnum(
  CustomerPotentialRanking,
);

export enum ProductComplexityRanking {
  Simple = 'Simple',
  Moderate = 'Moderate',
  Complex = 'Complex',
}

export const productComplexityRankingSchema = stringToEnum(
  ProductComplexityRanking,
);

export enum ConfiguratorSnapshotVersion {
  V1 = 'V1',
  V2 = 'V2',
}

export const configuratorSnapshotVersionSchema = stringToEnum(
  ConfiguratorSnapshotVersion,
);

export enum PartSpecTable {
  PART_BINARY = 'PART_BINARY',
  PART_CO_BINARY = 'PART_CO_BINARY',
}

export const partSpecTableSchema = stringToEnum(PartSpecTable);

export enum LeadTimeFormat {
  BOTH = 'BOTH',
  NUMBER = 'NUMBER',
  TEXT = 'TEXT',
}

export const leadTimeFormatSchema = stringToEnum(LeadTimeFormat);

export enum SnapQuoteStatus {
  NEW = 'NEW',
  INPROGRESS = 'INPROGRESS',
  SENT = 'SENT',
  DELETED = 'DELETED',
}

export const snapQuoteStatusSchema = stringToEnum(SnapQuoteStatus);

// NOTE - See parallel definition for Visual at lib/src/enums/quote-won-loss-status.ts
export enum SnapQuoteWonLossStatus {
  COMPLETED = 'COMPLETED',
  CONVERTED = 'CONVERTED',
  LOSS_CAPABILITIES = 'LOSS_CAPABILITIES',
  LOSS_CAPABILITIES_NO_QUOTE = 'LOSS_CAPABILITIES_NO_QUOTE',
  LOSS_CUSTOMER_LOST_BID = 'LOSS_CUSTOMER_LOST_BID',
  LOSS_EXCEPTIONS_NOT_ACCEPTED = 'LOSS_EXCEPTIONS_NOT_ACCEPTED',
  LOSS_LACKING_CERTIFICATION = 'LOSS_LACKING_CERTIFICATION',
  LOSS_LEAD_TIME = 'LOSS_LEAD_TIME',
  LOSS_PRICE = 'LOSS_PRICE',
  LOSS_QUOTING_FOR_MULTIPLE_CUSTOMERS = 'LOSS_QUOTING_FOR_MULTIPLE_CUSTOMERS',
  LOSS_QUOTING_FOR_MULTIPLE_REVS_PART_ITERATIONS = 'LOSS_QUOTING_FOR_MULTIPLE_REVS_PART_ITERATIONS',
  LOSS_ROUTINE_COMPETITIVE_CHECK = 'LOSS_ROUTINE_COMPETITIVE_CHECK',
  LOSS_TERMS_OF_SALE = 'LOSS_TERMS_OF_SALE',
  PAUSED = 'PAUSED',
  PENDING_CUSTOMER_RESPONSE = 'PENDING_CUSTOMER_RESPONSE',
  PENDING_MANUFACTURING = 'PENDING_MANUFACTURING',
  PENDING_OUTSIDE_PROCESS = 'PENDING_OUTSIDE_PROCESS',
  PENDING_REVISED_PO_CONTRACT_REVIEW = 'PENDING_REVISED_PO_CONTRACT_REVIEW',
  PENDING_REVISED_PO_PRICE = 'PENDING_REVISED_PO_PRICE',
  QUOTED = 'QUOTED',
  RFQ_RECEIVED = 'RFQ_RECEIVED',
  WIN_CAPABILITIES = 'WIN_CAPABILITIES',
  WIN_LEAD_TIME = 'WIN_LEAD_TIME',
  WIN_PRICE = 'WIN_PRICE',
}

export const snapQuoteWonLossStatusSchema = stringToEnum(
  SnapQuoteWonLossStatus,
);
